import { createFileRoute, Link } from '@tanstack/react-router'
import { useState, useEffect, useMemo } from 'react'
import { Eye, FileText, Lightbulb, Search, Users } from 'lucide-react'
import { ProtectedRoute } from '../../components/ProtectedRoute'
import { useAuth } from '../../hooks/useAuth'
import { can } from '../../lib/roles'
import { DEFAULT_COMPANY_ID } from '../../lib/config'
import { listUsers, type UserDto } from '../../lib/users'
import { listAllIDPs, type IDPPlan } from '../../lib/idp'
import { getBox9Employees, type Box9Employee } from '../../lib/box9grid'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/wawasan/')({
  component: WawasanIndexPage,
})

function WawasanIndexPage() {
  return (
    <ProtectedRoute minimumRole="manager">
      <WawasanIndexContent />
    </ProtectedRoute>
  )
}

const IDP_STATUS_STYLES: Record<string, string> = {
  draft:     'bg-stone-100 text-stone-600',
  active:    'bg-blue-50 text-blue-700 border border-blue-200',
  completed: 'bg-green-50 text-green-700 border border-green-200',
}

const QUADRANT_LABELS: Record<string, string> = {
  star:            'Star',
  'high-performer':'High Performer',
  solid:           'Solid',
  'future-star':   'Future Star',
  'high-potential':'High Potential',
  average:         'Average',
  'question-mark': 'Question Mark',
  underperformer:  'Underperformer',
  'low-performer': 'Low Performer',
}

const QUADRANT_STYLES: Record<string, string> = {
  star:            'bg-amber-50 text-amber-700 border border-amber-200',
  'high-performer':'bg-blue-50 text-blue-700 border border-blue-200',
  solid:           'bg-emerald-50 text-emerald-700 border border-emerald-200',
  'future-star':   'bg-purple-50 text-purple-700 border border-purple-200',
  'high-potential':'bg-indigo-50 text-indigo-700 border border-indigo-200',
  average:         'bg-gray-50 text-gray-600 border border-gray-200',
  'question-mark': 'bg-orange-50 text-orange-700 border border-orange-200',
  underperformer:  'bg-red-50 text-red-700 border border-red-200',
  'low-performer': 'bg-rose-50 text-rose-700 border border-rose-200',
}

function WawasanIndexContent() {
  const { user } = useAuth()
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID
  const isHROrAdmin = can(user?.role, 'manage:wawasan')
  const isManager = user?.role === 'manager'

  const [users, setUsers] = useState<UserDto[]>([])
  const [plans, setPlans] = useState<IDPPlan[]>([])
  const [positions, setPositions] = useState<Box9Employee[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')

  useEffect(() => {
    let cancelled = false
    Promise.all([
      listUsers(companyId, { isActive: true }),
      listAllIDPs(companyId),
      getBox9Employees(companyId),
    ]).then(([usersRes, plansRes, posRes]) => {
      if (cancelled) return
      if (usersRes.success && usersRes.data) setUsers(usersRes.data.users)
      if (plansRes.success && plansRes.data) setPlans(plansRes.data.plans)
      if (posRes.success && posRes.data) setPositions(posRes.data.employees)
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [companyId])

  // Managers only see their division; HR/admin see all
  const visibleUsers = useMemo(() => {
    let list = users
    if (isManager && user?.divisionId) {
      list = list.filter((u) => u.divisionId === user.divisionId)
    }
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(
        (u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q),
      )
    }
    return list
  }, [users, isManager, user?.divisionId, search])

  const getPlan = (userId: string) => plans.find((p) => p.userId === userId) ?? null
  const getPosition = (userId: string) => positions.find((p) => p.employeeId === userId) ?? null

  // Summary stats
  const totalVisible = visibleUsers.length
  const withIDP = useMemo(() => visibleUsers.filter((u) => getPlan(u.id)).length, [visibleUsers, plans])
  const positioned = useMemo(() => visibleUsers.filter((u) => getPosition(u.id)).length, [visibleUsers, positions])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-navy-50">
          <Eye className="w-5 h-5 text-navy-600" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-foreground">Talent Overview</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isManager ? 'Your division\'s talent intelligence' : 'Company-wide talent intelligence'}
          </p>
        </div>
      </div>

      {/* Summary cards */}
      {!loading && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-card border rounded-lg p-4">
            <p className="text-xs text-muted-foreground">
              {isManager ? 'Division Members' : 'Total Employees'}
            </p>
            <p className="text-2xl font-bold text-foreground mt-1">{totalVisible}</p>
          </div>
          <div className="bg-card border rounded-lg p-4">
            <p className="text-xs text-muted-foreground">Have IDP</p>
            <p className="text-2xl font-bold text-foreground mt-1">
              {withIDP}
              <span className="text-sm font-normal text-muted-foreground ml-1">
                / {totalVisible}
              </span>
            </p>
          </div>
          <div className="bg-card border rounded-lg p-4">
            <p className="text-xs text-muted-foreground">Positioned in 9-Box</p>
            <p className="text-2xl font-bold text-foreground mt-1">
              {positioned}
              <span className="text-sm font-normal text-muted-foreground ml-1">
                / {totalVisible}
              </span>
            </p>
          </div>
        </div>
      )}

      {/* Search + table */}
      <div className="bg-card border rounded-xl overflow-hidden">
        {/* Toolbar */}
        <div className="flex items-center gap-3 px-4 py-3 border-b bg-muted/30">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search employees…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 text-sm bg-background border rounded-md focus:outline-none focus:ring-1 focus:ring-primary/40"
            />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Link to={'/wawasan/9box' as any}>
              <Button size="sm" variant="outline" className="text-xs">
                9-Box Grid
              </Button>
            </Link>
            {isHROrAdmin && (
              <Link to={'/reports' as any}>
                <Button size="sm" variant="outline" className="text-xs">
                  AI Reports
                </Button>
              </Link>
            )}
          </div>
        </div>

        {/* Table */}
        {loading ? (
          <div className="divide-y">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-3 animate-pulse">
                <div className="w-8 h-8 rounded-full bg-muted shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-3 bg-muted rounded w-36" />
                  <div className="h-2.5 bg-muted rounded w-24" />
                </div>
                <div className="h-5 bg-muted rounded w-20" />
                <div className="h-5 bg-muted rounded w-24" />
                <div className="h-7 bg-muted rounded w-28" />
              </div>
            ))}
          </div>
        ) : visibleUsers.length === 0 ? (
          <div className="flex flex-col items-center py-16 text-center">
            <Users className="w-8 h-8 text-muted-foreground/40 mb-3" />
            <p className="text-sm text-muted-foreground">
              {search ? 'No employees match your search' : 'No employees found'}
            </p>
          </div>
        ) : (
          <div className="divide-y">
            {/* Header row */}
            <div className="grid grid-cols-[1fr_auto_auto_auto] lg:grid-cols-[1fr_160px_160px_200px] gap-4 px-4 py-2 bg-muted/20">
              <p className="text-xs font-medium text-muted-foreground">Employee</p>
              <p className="text-xs font-medium text-muted-foreground hidden lg:block">9-Box</p>
              <p className="text-xs font-medium text-muted-foreground hidden lg:block">IDP Status</p>
              <p className="text-xs font-medium text-muted-foreground text-right">Actions</p>
            </div>

            {visibleUsers.map((emp) => {
              const plan = getPlan(emp.id)
              const pos = getPosition(emp.id)
              const initials = emp.name.split(' ').map((n) => n[0]).join('').slice(0, 2).toUpperCase()

              return (
                <div
                  key={emp.id}
                  className="grid grid-cols-[1fr_auto] lg:grid-cols-[1fr_160px_160px_200px] gap-4 px-4 py-3 items-center hover:bg-muted/20 transition-colors"
                >
                  {/* Employee info */}
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-navy-100 text-navy-700 flex items-center justify-center text-xs font-semibold shrink-0">
                      {initials}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">{emp.name}</p>
                      <p className="text-xs text-muted-foreground truncate">{emp.email}</p>
                    </div>
                  </div>

                  {/* 9-Box quadrant — hidden on mobile */}
                  <div className="hidden lg:block">
                    {pos ? (
                      <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium', QUADRANT_STYLES[pos.quadrant] ?? 'bg-muted text-muted-foreground')}>
                        {QUADRANT_LABELS[pos.quadrant] ?? pos.quadrant}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </div>

                  {/* IDP status — hidden on mobile */}
                  <div className="hidden lg:block">
                    {plan ? (
                      <span className={cn('text-xs px-2 py-0.5 rounded-full font-medium capitalize', IDP_STATUS_STYLES[plan.status] ?? 'bg-muted text-muted-foreground')}>
                        {plan.status}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">No IDP</span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1.5 justify-end">
                    <Link to={'/idp' as any}>
                      <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-muted-foreground hover:text-foreground">
                        <Lightbulb className="w-3 h-3" />
                        <span className="hidden sm:inline">IDP</span>
                      </Button>
                    </Link>
                    {isHROrAdmin && (
                      <Link to={'/reports' as any} search={{ employee: emp.name } as any}>
                        <Button size="sm" variant="ghost" className="h-7 text-xs gap-1 text-muted-foreground hover:text-foreground">
                          <FileText className="w-3 h-3" />
                          <span className="hidden sm:inline">Report</span>
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
